import React, { useEffect, useMemo, useRef } from 'react';

import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { formatIsoToDate } from '../../utils';
import { getExpenseType } from '../../utils/utilities';
import TablePagination from '../TablePagination';

import './styles.scoped.css';

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const datetime = (a, b) => {
  const aDate = new Date(a.createdAt).getTime();
  const bDate = new Date(b.createdAt).getTime();

  if (aDate < bDate) {
    return -1;
  }
  if (aDate > bDate) {
    return 1;
  }
  return 0;
};

const EditExpenseButton = (expense, to) => {
  const thisExpense = expense.data[parseInt(expense.cell.row.id)];

  const expenseType = getExpenseType(thisExpense);
  return (
    <Link to={`${to}/edit-${expenseType}/${thisExpense.id}`}>
      <MdEdit />
    </Link>
  );
};

const ResultsTable = ({ data, loading, to }) => {
  const columnClassName = 'align-middle text-wrap ';

  // CP-859: this component used to kick off the search's paging drain from its own mount effect
  // (`useEffect(() => { getBatch(); }, [])`), while the page it belongs to also fetched page 1 -
  // so every search fetched its first page twice and discarded one of them. The drain now lives
  // entirely in SearchExpenses/index.js, which is also what lets results paint per page.
  //
  // `const memoData = useMemo(() => data, [data])` was also removed: memoising a value on itself
  // does nothing.

  const columns = useMemo(
    () => [
      {
        Header: 'Expense ID',
        accessor: 'id',
        className: columnClassName,
      },
      {
        Header: 'Driver Name & Number',
        accessor: 'driverNameNumber',
        className: columnClassName,
      },
      {
        Header: 'Assignment / Contract Act.',
        accessor: 'assignmentId',
        className: columnClassName,
      },
      {
        Header: 'Customer Name & Number',
        accessor: 'customerNameNumber',
        className: columnClassName,
      },
      {
        Header: 'Date Entered',
        accessor: 'createdAt',
        className: columnClassName,
        Cell: instance => `${formatIsoToDate(instance.value) || ''}`,
      },
      {
        Header: 'Start Date',
        accessor: 'startDate',
        className: columnClassName,
        Cell: instance => `${formatIsoToDate(instance.value) || ''}`,
      },
      {
        Header: 'End Date',
        accessor: 'endDate',
        className: columnClassName,
        Cell: instance => `${formatIsoToDate(instance.value) || ''}`,
      },
      {
        Header: 'Pay Week Ending',
        accessor: 'payWeekEnding',
        className: columnClassName,
        Cell: instance => `${formatIsoToDate(instance.value) || ''}`,
      },
      {
        Header: 'Pay Code',
        accessor: 'code',
        className: columnClassName,
      },
      {
        Header: 'Status',
        accessor: 'status',
        className: columnClassName,
      },
      {
        Header: 'Bill Amount',
        accessor: 'totalToBeBilled',
        className: columnClassName,
      },
      {
        Header: '',
        accessor: 'editExpense',
        className: columnClassName,
        Cell: instance => EditExpenseButton(instance, to),
      },
    ],
    // CP-859: `to` is closed over by the edit-link cell above but was missing from the deps, so
    // the column definitions kept whatever value it had at mount.
    [to],
  );

  // CP-859: react-table v7 defaults autoResetPage/autoResetSortBy (and friends) to true, so every
  // change of `data` throws the user back to page 1. That was harmless while this table received
  // its rows exactly once, at the end of the drain - but rows now append per page, so without this
  // a user who starts reading gets bounced to page 1 on every batch, dozens of times in one
  // search. That would read as worse than the spinner it replaced.
  //
  // Suppressed only while the drain is running: once `loading` is false a user-initiated sort or
  // filter resets the page normally, which is what they expect. Same idea as the
  // skipPageResetRef pattern in CommonTable/index.js.
  //
  // CP-859 follow-up: `const skipReset = loading` was one render too early. The FINAL batch
  // arrives with `loading` already flipped to false, so that render carried both a new `data`
  // reference and `autoResetPage: true` - and react-table reset the page. Paging held through
  // every intermediate batch and then bounced the user to page 1 at the exact moment the search
  // completed, which is the most visible moment there is. Verified on dev 2026-09-16: the reset
  // and the spinner disappearing land in the same commit.
  //
  // So suppress on any render where `data` actually changed identity, not just while loading.
  // The refs are advanced in a plain `useEffect`, which runs AFTER react-table's
  // `useMountedLayoutEffect` - so on the render that carries new data we still compare against
  // the previously committed value and correctly report a change.
  const prevDataRef = useRef(data);
  const dataChanged = prevDataRef.current !== data;

  useEffect(() => {
    prevDataRef.current = data;
  });

  const skipReset = loading || dataChanged;

  const tableInstance = useTable(
    {
      columns,
      data,
      autoResetPage: !skipReset,
      autoResetSortBy: !skipReset,
      autoResetFilters: !skipReset,
      autoResetGlobalFilter: !skipReset,
      initialState: {
        sortBy: [
          {
            id: 'createdAt',
            desc: true,
          },
        ],
      },
      sortTypes: {
        datetime,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}>
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid className="table-container">
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row style={{ border: '1px solid #343a40', borderRadius: '10px', backgroundColor: 'white' }}>
        <Col>
          <div
            style={{
              color: 'red',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '10px',
            }}>
            {loading && <Spinner animation="border" />}
          </div>
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
};

export default ResultsTable;
